import { type ReactElement } from 'react';
import { redirect, useLoaderData } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AuthWall, Card } from '@marketplace/ui';
import { type CategorySummary } from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { billingStepPath } from '../features/billing/billing.js';
import { ProviderForm, type ProviderFormActionData } from '../features/providers/ProviderForm.js';
import { parseProviderForm } from '../features/providers/signup.js';
import { type TranslationKey } from '../i18n/index.js';
import { ApiError } from '../shared/api.js';
import { loadCategories } from '../shared/categories.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * The supply side — ADR-011 §2 puts `/:lang/become-a-pro` in `W12-T10` because the home page's CTA
 * needs somewhere to go, and a marketplace with no providers has nothing to sell. Half the
 * cold-start answer (`R3`) is a reason for a tradesperson to sign up.
 *
 * The segment is not translated: `/es/become-a-pro`, the same rule `/es/search` follows
 * (ADR-011 Amendment 1).
 *
 * **`W2-T05` put the form here.** Signed out, the page is what it was — the benefits and the auth
 * wall, because an account is a client account first (`ADR-005` Q1). Signed in, the wall becomes the
 * provider profile form, and one submit creates the profile *and* the `PROVIDER` role in one
 * transaction (`POST /api/providers/me`). Somebody who already has a profile is sent to
 * `/account/pro`, where they can see and edit it (§7).
 *
 * **`W5-T01` added a last step.** A submit lands on `/account/payouts?step=registration`, which offers
 * Connect with Stripe and Skip for now, instead of the feed. A provider is paid through a connected
 * account, and quoting waits until Stripe clears one (spec §4.3, AC 38).
 *
 * **`W5-T13` put the subscription before it** (AC 25): a submit lands on
 * `/account/billing?step=registration`, and Connect follows the payment. A provider is a subscriber
 * (ADR-017 §1); the profile comes first only because the subscription is keyed on it (`W5-T07` E1).
 *
 * Spec: `docs/specs/S2/W2-T05-become-a-provider.md` §2, §3.5.
 */
const BENEFITS: { key: string; title: TranslationKey; body: TranslationKey }[] = [
  { key: 'leads', title: 'pro.benefits.leads.title', body: 'pro.benefits.leads.body' },
  { key: 'control', title: 'pro.benefits.control.title', body: 'pro.benefits.control.body' },
  { key: 'payment', title: 'pro.benefits.payment.title', body: 'pro.benefits.payment.body' },
];

type BecomeAProData =
  | { locale: string; state: 'signed-out' }
  | { locale: string; state: 'form'; categories: CategorySummary[] };

export async function loader({ params, context }: LoaderFunctionArgs): Promise<BecomeAProData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) return { locale, state: 'signed-out' };

  const { queryClient, api } = context.get(routeContext);
  // `fetchQuery`, not `ensureQueryData`: after the action this must reflect the write it just made
  // (`MEM-2026-09-14-3`). A failure here is not "no profile" — it falls through to the form, and the
  // API's `409` is the backstop if they turn out to have one.
  const own = await queryClient
    .fetchQuery({
      queryKey: queryKeys.myProviderProfile(),
      queryFn: () => api.getMyProviderProfile(),
    })
    .catch(() => null);
  if (own !== null) throw redirect(`/${locale}/account/pro`);

  return { locale, state: 'form', categories: await loadCategories(context, locale) };
}

export async function action({
  params,
  request,
  context,
}: ActionFunctionArgs): Promise<ProviderFormActionData | Response> {
  const locale = localeOf(params);
  const parsed = parseProviderForm(await request.formData());
  if (!parsed.ok) return { errors: parsed.errors };

  const { queryClient, api } = context.get(routeContext);

  try {
    await api.createProviderProfile(parsed.input);
  } catch (error) {
    // `409` is "you already have a profile" — they are a provider either way, so it goes where a
    // success goes. Anything else keeps the form and what was typed.
    if (!(error instanceof ApiError && error.status === 409)) {
      return { failure: 'proForm.error.failed' };
    }
  }

  // `refetchType: 'all'` — an unobserved query is otherwise only marked stale, and the next loader
  // hands the stale answer back (`MEM-2026-09-27-jobs-1`). The feed matters most: before this
  // submit it was a `403`.
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.myProviderProfile(),
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({ queryKey: queryKeys.jobFeed(), refetchType: 'all' }),
  ]);
  return redirect(billingStepPath(locale));
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const data = useLoaderData<BecomeAProData>();
  const { locale } = data;

  return (
    <article data-testid="become-a-pro">
      <h1>{t('pro.title')}</h1>
      <p className="mp-lead">{t('pro.intro')}</p>

      <section className="mp-section" aria-labelledby="pro-benefits">
        <h2 id="pro-benefits">{t('pro.benefits.title')}</h2>
        <ul className="mp-card-grid" role="list">
          {BENEFITS.map((benefit) => (
            <li key={benefit.key}>
              <Card title={t(benefit.title)} description={t(benefit.body)} />
            </li>
          ))}
        </ul>
      </section>

      {data.state === 'signed-out' ? (
        // An account is a client account first (`ADR-005` Q1); the form is on the other side of it.
        <AuthWall
          title={t('pro.title')}
          description={t('pro.pending')}
          action={{ label: t('auth.signup.title'), href: `/${locale}/signup` }}
        />
      ) : (
        <section className="mp-section" aria-labelledby="pro-form-title">
          <h2 id="pro-form-title">{t('proForm.title')}</h2>
          <p className="mp-lead">{t('proForm.intro')}</p>
          <ProviderForm
            categories={data.categories}
            testId="provider-signup-form"
            submitLabel={t('proForm.submit')}
            pendingLabel={t('proForm.pending')}
          />
        </section>
      )}
    </article>
  );
}
